import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, TextField, Button, Alert, CircularProgress, Box,
  Typography, Paper, Grid, Card, CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

const  CreateRole = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/roles/create' } });
      return;
    }
    fetchPermissions();
  }, [navigate]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.PERMISSIONS.BASE, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const permissionsData = Array.isArray(response.data.data) ? response.data.data : response.data.data || [];
      
      const initializedPermissions = permissionsData.map(perm => ({
        ...perm,
        actions: {
          add: false,
          edit: false,
          delete: false,
          view: false
        }
      }));
      setPermissions(initializedPermissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError(error.response?.data?.message || 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (permissionId, isChecked) => {
    setPermissions(prev =>
      prev.map(perm =>
        perm.id === permissionId
          ? {
              ...perm,
              actions: {
                add: isChecked,
                edit: isChecked,
                delete: isChecked,
                view: isChecked
              }
            }
          : perm
      )
    );
    
    const permission = permissions.find(p => p.id === permissionId);
    updateSelectedPermissions(
      permission.name,
      isChecked ? ['add', 'edit', 'delete', 'view'] : []
    );
  };

  const handleToggle = (permissionId, action, isChecked) => {
    setPermissions(prev =>
      prev.map(perm =>
        perm.id === permissionId
          ? {
              ...perm,
              actions: {
                ...perm.actions,  
                [action]: isChecked
              }
            }
          : perm
      )
    );

    const permission = permissions.find(p => p.id === permissionId);
    const updatedActions = Object.entries({
      ...permission.actions,
      [action]: isChecked
    })
      .filter(([_, value]) => value)
      .map(([key]) => key);

    updateSelectedPermissions(permission.name, updatedActions);
  };

  const updateSelectedPermissions = (permissionName, actions) => {
    setSelectedPermissions(prev => {
      const newPermissions = { ...prev };
      if (actions.length > 0) {
        newPermissions[permissionName] = actions;
      } else {
        delete newPermissions[permissionName]; // Remove permission if no actions are selected
      }
      return newPermissions;
    });
  };

  const handleCreateRole = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: '/roles/create' } });
        return;
      }

      if (!roleName.trim()) {
        setError('Role name is required');
        setLoading(false);
        return;
      }

      // Convert selected permissions to the format expected by the backend
      const permissionNames = Object.entries(selectedPermissions)
        .flatMap(([permissionName, actions]) =>
          actions.map(action => `${action}-${permissionName}`)
        );

      if (permissionNames.length === 0) {
        setError('Please select at least one permission');
        setLoading(false);
        return;
      }

      // Create the role
      const roleResponse = await axios.post(API_ENDPOINTS.ROLES.BASE, {
        name: roleName.trim(),
        guard_name: 'web'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const roleId = roleResponse.data.data.id;

      // Assign permissions to the role
      await axios.post(`${API_ENDPOINTS.ROLES.BASE}/${roleId}/permissions`, {
        permissions: permissionNames
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      navigate('/roles');
    } catch (error) {
      console.error('Error creating role:', error);
      console.log('Error response:', error.response?.data);
      setError(error.response?.data?.message || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/roles')} sx={{ mr: 2 }}>
              Back to Roles
            </Button>
            <Typography variant="h5">Create New Role</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Role Name"
                    fullWidth
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Select Permissions</Typography>
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Permission Name</TableCell>
                            <TableCell>All</TableCell>
                            <TableCell>Add</TableCell>
                            <TableCell>Edit</TableCell>
                            <TableCell>Delete</TableCell>
                            <TableCell>View</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {permissions.map((permission) => (
                            <TableRow key={permission.id}>
                              <TableCell>{permission.name}</TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={Object.values(permission.actions).every(v => v)}
                                  onChange={(e) => handleSelectAll(permission.id, e.target.checked)}
                                />
                              </TableCell>
                              {['add', 'edit', 'delete', 'view'].map((action) => (
                                <TableCell key={action}>
                                  <Checkbox
                                    checked={permission.actions[action]}
                                    onChange={(e) => handleToggle(permission.id, action, e.target.checked)}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      onClick={handleCreateRole} 
                      variant="contained" 
                      color="primary"
                      disabled={!roleName || loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Create Role'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CreateRole;